import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import './index.css';
import DateCarousel from '../../components/classes/DateCarousel';
import FilterQuery from '../../components/classes/FilterQuery';
import ClassWrapper from '../../components/classes/ClassWrapper';
import Filters from '../../components/classes/Filters';
import { Info, Filter as FilterIcon } from 'lucide-react';
import { fetchSchedules, type ScheduleFilters } from '../../services/schedule.service';
import type { ClassInterface } from '../../types/class.interface';
import { Loader } from '@beholos/holoscommon';
import { format } from 'date-fns';
import { usePageTitle } from '../../hooks/usePageTitle';

interface ClassesProps {
  showNutritionist?: boolean;
  showHealthCoach?: boolean;
  showFinancialAdvisor?: boolean;
  showPsychologist?: boolean;
  showPersonalTrainer?: boolean;
}

export default function Classes({
  showNutritionist = false,
  showHealthCoach = false,
  showFinancialAdvisor = false,
  showPsychologist = false,
  showPersonalTrainer = false
}: ClassesProps) {
  usePageTitle('Clases - Holos');
  const location = useLocation();
  
  const [query, setQuery] = useState('');
  const [classes, setClasses] = useState<ClassInterface[]>([]);
  const [loading, setLoading] = useState(true); // first page or full refresh
  const [paginating, setPaginating] = useState(false); // subsequent pages
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  const [stagedFilters, setStagedFilters] = useState<ScheduleFilters | null>(null);
  const [filters, setFilters] = useState<ScheduleFilters>({
    page: 1,
    limit: 20,
    date: format(new Date(), 'yyyy-MM-dd') // Use date-fns for consistent date formatting
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlQuery = params.get('query') || '';
    if (urlQuery && !query) {
      setQuery(urlQuery);
    }
  }, [location.search]);

  // Prevent body scrolling while on the classes screen
  useEffect(() => {
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, []);

  const clearFilterQuery = () => {
    setQuery('');
  };

  const fetchClasses = useCallback(async (currentFilters: ScheduleFilters, searchQuery: string) => {
    try {
      const isLoadingMore = currentFilters.page && currentFilters.page > 1;
      if (isLoadingMore) {
        setPaginating(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Add search query and component props to filters
      const filtersWithQuery = {
        ...currentFilters,
        ...(searchQuery.trim() && { name: searchQuery.trim() }),
        ...(showNutritionist && { showNutritionist }),
        ...(showHealthCoach && { showHealthCoach }),
        ...(showFinancialAdvisor && { showFinancialAdvisor }),
        ...(showPsychologist && { showPsychologist }),
        ...(showPersonalTrainer && { showPersonalTrainer })
      };

      const data = await fetchSchedules(filtersWithQuery);

      // Diagnostics: show modality flags for fetched schedules
      try {
        console.log('Fetched schedules modality', data.map((c) => ({
          id: c.id || c._id,
          title: c.title,
          liveStreamClass: !!c.liveStreamClass,
        })));
      } catch {}

      // API now provides isFavourite; normalize to isFavorite in our model
      const normalized = data.map(cls => ({
        ...cls,
        isFavorite: (cls as any).isFavorite ?? (cls as any).isFavourite ?? false,
      } as ClassInterface));

      setClasses(prev => isLoadingMore ? [...prev, ...normalized] : normalized);

      // Determine if there are more items to load
      if (filtersWithQuery.limit) {
        setHasMore(data.length === filtersWithQuery.limit);
      } else {
        setHasMore(data.length > 0);
      }
    } catch (err) {
      console.error('Error fetching classes:', err);
      setError(err instanceof Error ? err.message : 'Error fetching classes');
      setClasses([]);
    } finally {
      setLoading(false);
      setPaginating(false);
    }
  }, [showNutritionist, showHealthCoach, showFinancialAdvisor, showPsychologist, showPersonalTrainer]);

  // Hydrate filters from storage if user opted to save filters
  useEffect(() => {
    try {
      const savePref = localStorage.getItem('holos-save-filters-preference');
      if (savePref === 'true') {
        const saved = localStorage.getItem('holos-classes-filters');
        if (saved) {
          const parsed = JSON.parse(saved);
          setFilters(prev => ({ ...prev, ...parsed }));
        }
      }
    } catch (e) {
      console.error('Error restoring classes filters:', e);
    }
  }, []);

  // Initial load handled by filters-effect below (debounced) to avoid duplicate requests

  // Fetch classes when filters change
  useEffect(() => {
    if (!filters.date) return;

    // For page 1 (new search/filters), show full-page loader with debounce
    if (!filters.page || filters.page === 1) {
      setLoading(true);
      const timeoutId = setTimeout(() => {
        fetchClasses(filters, query);
      }, 1000);
      return () => clearTimeout(timeoutId);
    }

    // For pagination (page > 1), do not show full-page loader; append items
    fetchClasses(filters, query);
  }, [query, filters, fetchClasses]);

  // Intersection Observer for infinite scrolling
  const listContainerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = listContainerRef.current;
    const sentinel = sentinelRef.current;
    if (!container || !sentinel) return;

    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries;
        if (entry.isIntersecting && !loading && hasMore) {
          setFilters(prev => ({ ...prev, page: (prev.page || 1) + 1 }));
        }
      },
      {
        root: container,
        rootMargin: '200px',
        threshold: 0.1
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loading, hasMore]);

  const handleFilterChange = useCallback((newFilters: Partial<ScheduleFilters>) => {
    // If online modality is selected, ignore location-based filters
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      ...(newFilters.liveStreamClass === true ? { cities: undefined, districts: undefined } : {}),
      page: 1
    })); // Reset to first page when filters change
  }, []);

  const handleDateChange = useCallback((date: string) => {
    console.log('handleDateChange Date selected:', date);
    handleFilterChange({ date });
  }, [handleFilterChange]);

  // Persist minimal filters (date and modality) if preference enabled
  useEffect(() => {
    try {
      const savePref = localStorage.getItem('holos-save-filters-preference');
      if (savePref === 'true') {
        const minimal: Partial<ScheduleFilters> = {
          date: filters.date,
          liveStreamClass: filters.liveStreamClass,
        };
        localStorage.setItem('holos-classes-filters', JSON.stringify(minimal));
      }
    } catch (e) {
      console.error('Error saving classes filters:', e);
    }
  }, [filters.date, filters.liveStreamClass]);

  const handleSearchChange = useCallback((searchQuery: string) => {
    setQuery(searchQuery);
    // Reset to first page whenever a new search query is entered
    setFilters(prev => ({ ...prev, page: 1 }));
  }, []);

  return (
    <div className="flex h-[calc(100vh-83px)] w-screen overflow-hidden">
      <div className="hidden md:block md:w-1/4 h-full overflow-y-auto">
        <aside className="flex flex-col w-full bg-sand h-full border-r border-gray-400">
          <div className="flex-grow overflow-y-auto px-4">
            <Filters
              showNutritionist={showNutritionist}
              showHealthCoach={showHealthCoach}
              showFinancialAdvisor={showFinancialAdvisor}
              showPsychologist={showPsychologist}
              showPersonalTrainer={showPersonalTrainer}
              onFilterChange={handleFilterChange}
              filters={filters}
            />
          </div>
        </aside>
      </div>

      <div className="flex-1 h-full overflow-hidden bg-sand p-6 flex flex-col">
        <FilterQuery
          query={query}
          setQuery={handleSearchChange}
          clearFilterQuery={clearFilterQuery}
        />
        {/* Mobile Filters trigger */}
        <div className="md:hidden mb-3">
          <button
            type="button"
            className="w-full rounded-xl border border-black bg-transparent text-black font-semibold py-2 flex items-center justify-center gap-2 cursor-pointer"
            onClick={() => {
              setStagedFilters(filters);
              setIsFiltersModalOpen(true);
            }}
          >
            <FilterIcon className="w-4 h-4" />
            Filtros
          </button>
        </div>
        <DateCarousel onDateChange={handleDateChange} selectedDate={filters.date} />


        <div className="flex-1 overflow-y-auto" ref={listContainerRef}>
          <div className="my-1">
            <small className='flex flex-row items-center'>
              <Info className='mr-1 ' />
              Todos los horarios se muestran en tu zona horaria local.
            </small>
          </div>
          {/* Full-page loader only for first page loads; small loader for pagination */}
          {loading && !paginating ? (
            <div className="flex justify-center items-center h-full">
              <Loader />
            </div>
          ) : error ? (
            <div className="flex justify-center items-center h-full">
              <div className="text-center">
                <p className="text-red-500 mb-2">Error: {error}</p>
                <button 
                  onClick={() => fetchClasses(filters, query)}
                  className="px-4 py-2 bg-holos text-white rounded hover:bg-holos-dark cursor-pointer"
                >
                  Reintentar
                </button>
              </div>
            </div>
          ) : classes.length === 0 ? (
            <div className="flex justify-center items-center h-full">
              <div className="text-center">
                <p className="text-gray-500 mb-2">No se encontraron clases</p>
                <p className="text-sm text-gray-400">Intenta ajustar los filtros de búsqueda</p>
              </div>
            </div>
          ) : (
            <>
              <ClassWrapper
                classes={classes}
                liveStreamClassFilter={filters.liveStreamClass}
                onFavoriteChange={(classId, isFavorite) => {
                  // Update local item only; server remains source of truth
                  setClasses(prev => prev.map(cls => cls._id === classId ? { ...cls, isFavorite } : cls));
                }}
              />
              {/* Sentinel element for IntersectionObserver */}
              <div ref={sentinelRef} className="h-1" />
              {paginating && (
                <div className="flex justify-center py-4">
                  <Loader />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* Mobile full-screen Filters Modal */}
      {isFiltersModalOpen && stagedFilters && (
        <div className="fixed inset-0 z-50 md:hidden bg-sand">
          <div className="h-full flex flex-col">
            <div className="p-4 border-b border-gray-300 flex items-center justify-between">
              <div className="font-juana font-bold text-lg text-holos-dark">Filtros</div>
              <button
                type="button"
                className="text-2xl leading-none cursor-pointer"
                onClick={() => setIsFiltersModalOpen(false)}
              >
                &times;
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <Filters
                showNutritionist={showNutritionist}
                showHealthCoach={showHealthCoach}
                showFinancialAdvisor={showFinancialAdvisor}
                showPsychologist={showPsychologist}
                showPersonalTrainer={showPersonalTrainer}
                onFilterChange={(newFilters) =>
                  setStagedFilters(prev => ({ ...(prev as ScheduleFilters), ...newFilters, page: 1 }))
                }
                filters={stagedFilters}
              />
            </div>
            <div className="p-4 border-t border-gray-300 bg-sand">
              <div className="flex gap-3">
                <button
                  type="button"
                  className="flex-1 rounded-xl border border-black bg-transparent text-black font-semibold py-3 cursor-pointer"
                  onClick={() => setIsFiltersModalOpen(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="flex-1 rounded-xl bg-holos text-white font-semibold py-3 cursor-pointer"
                  onClick={() => {
                    handleFilterChange(stagedFilters);
                    setIsFiltersModalOpen(false);
                  }}
                >
                  Aplicar cambios
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
